import type { LiteObject, PART_STATUSES } from '@frontend/api';
import type { UNITS } from '@frontend/partviewer/src/constants';
import type { STEPBody } from '@frontend/partviewer/src/loaders/step/STEPBody';
import type { Comparison } from '@frontend/partviewer/src/renderer/caliper-utils';
import type {
  UpdateVectorOperation,
  Selection,
  MetaData,
  ForceView,
} from '@frontend/partviewer/src/renderer/renderer-component';
import type { ViewerDataResponseJson } from '@frontend/partviewer/src/viewerDataService';

import { notify } from 'utils/reduxUtils';

import type { EDITOR_MODES } from './constants';

export const types = {
  FINALIZE_SET_SCALE: 'vectorEditor/FINALIZE_SET_SCALE',
  RENDERER_RECEIVE_UPDATED_VECTORS:
    'vectorEditor/RENDERER_RECEIVE_UPDATED_VECTORS',
  RENDERER_RECEIVE_UPDATED_VECTOR_STYLE_BY_NAME_MAP:
    'vectorEditor/RENDERER_RECEIVE_UPDATED_VECTOR_STYLE_BY_NAME_MAP',
  RENDERER_INITIALIZE_VECTOR_STYLE_BY_NAME_MAP:
    'vectorEditor/RENDERER_INITIALIZE_VECTOR_STYLE_BY_NAME_MAP',
  RENDERER_RECEIVE_SERIALIZED_DXF:
    'vectorEditor/RENDERER_RECEIVE_SERIALIZED_DXF',
  RENDERER_RECEIVE_ERROR: 'vectorEditor/RENDERER_RECEIVE_ERROR',

  EDITOR_RECEIVE_UPDATED_VECTORS: 'vectorEditor/EDITOR_RECEIVE_UPDATED_VECTORS',
  EDITOR_INCREMENT_UPDATED_VECTORS_COUNTER:
    'vectorEditor/EDITOR_INCREMENT_UPDATED_VECTORS_COUNTER',
  EDITOR_RECEIVE_THICKNESS: 'vectorEditor/EDITOR_RECEIVE_THICKNESS',
  EDITOR_RECEIVE_HAS_EXPORT_VECTORS:
    'vectorEditor/EDITOR_RECEIVE_HAS_EXPORT_VECTORS',
  REDO: 'vectorEditor/REDO',
  UNDO: 'vectorEditor/UNDO',

  INIT_VECTOR_EDITOR: 'vectorEditor/INIT_VECTOR_EDITOR',

  SAVE_VECTOR_FILE_REQUEST: 'vectorEditor/SAVE_VECTOR_FILE_REQUEST',
  SAVE_VECTOR_FILE_SUCCESS: 'vectorEditor/SAVE_VECTOR_FILE_SUCCESS',
  SAVE_VECTOR_FILE_ERROR: 'vectorEditor/SAVE_VECTOR_FILE_ERROR',
  SAVE_VECTOR_FILE_IS_MODAL_OPEN: 'vectorEditor/SAVE_VECTOR_FILE_IS_MODAL_OPEN',
  SAVE_VECTOR_FILE_IS_MODAL_CLOSED:
    'vectorEditor/SAVE_VECTOR_FILE_IS_MODAL_CLOSED',

  MEASUREMENT_TOOL_ENABLE: 'vectorEditor/MEASUREMENT_TOOL_ENABLE',
  MEASUREMENT_TOOL_RECEIVE_COMPARISON:
    'vectorEditor/MEASUREMENT_TOOL_RECEIVE_COMPARISON',
  MEASUREMENT_TOOL_RECEIVE_MEASUREMENT:
    'vectorEditor/MEASUREMENT_TOOL_RECEIVE_MEASUREMENT',
  MEASUREMENT_TOOL_RECEIVE_SELECTIONS:
    'vectorEditor/MEASUREMENT_TOOL_RECEIVE_SELECTIONS',
  MEASUREMENT_TOOL_REMOVE_SELECTIONS:
    'vectorEditor/MEASUREMENT_TOOL_REMOVE_SELECTIONS',

  RECEIVE_VECTORIZATION_STATUS: 'vectorEditor/RECEIVE_VECTORIZATION_STATUS',
  RECEIVE_EDITOR_MODE: 'vectorEditor/RECEIVE_EDITOR_MODE',
  RECEIVE_FORCE_VIEW: 'vectorEditor/RECEIVE_FORCE_VIEW',
  RECEIVE_META_DATA: 'vectorEditor/RECEIVE_META_DATA',
  RECEIVE_UNITS: 'vectorEditor/RECEIVE_UNITS',
  RECEIVE_UNITS_SCALE_THICKNESS: 'vectorEditor/RECEIVE_UNITS_SCALE_THICKNESS',
  RECEIVE_VIEWER_DATA: 'vectorEditor/RECEIVE_VIEWER_DATA',

  RESET: 'vectorEditor/RESET',

  SELECTOR_TOOL_BOX_SELECT: 'vectorEditor/SELECTOR_TOOL_BOX_SELECT',
  SELECTOR_TOOL_CLEAR_ALL_SELECTIONS:
    'vectorEditor/SELECTOR_TOOL_CLEAR_ALL_SELECTIONS',
  SELECTOR_TOOL_ENABLE: 'vectorEditor/SELECTOR_TOOL_ENABLE',
  SELECTOR_TOOL_RECEIVE_CUMULATIVE_INFO:
    'vectorEditor/SELECTOR_TOOL_RECEIVE_CUMULATIVE_INFO',
  SELECTOR_TOOL_RECEIVE_SELECTION:
    'vectorEditor/SELECTOR_TOOL_RECEIVE_SELECTION',
  SELECTOR_TOOL_REMOVE_SELECTION: 'vectorEditor/SELECTOR_TOOL_REMOVE_SELECTION',
  SELECTOR_TOOL_RECEIVE_SELECTIONS:
    'vectorEditor/SELECTOR_TOOL_RECEIVE_SELECTIONS',
  SELECTOR_TOOL_REMOVE_SELECTIONS:
    'vectorEditor/SELECTOR_TOOL_REMOVE_SELECTIONS',

  SET_PANEL_STATE: 'partLibrary/viewer/SET_PANEL_STATE',

  UPDATE_PAGE_VECTORIZATION_REQUEST:
    'vectorEditor/UPDATE_PAGE_VECTORIZATION_REQUEST',
  UPDATE_PAGE_VECTORIZATION_SUCCESS:
    'vectorEditor/UPDATE_PAGE_VECTORIZATION_SUCCESS',
  UPDATE_PAGE_VECTORIZATION_ERROR:
    'vectorEditor/UPDATE_PAGE_VECTORIZATION_ERROR',
};

// for renderer (pass through to PartViewer)
export const vectorizationRenderer = {
  init: ({
    partFileName,
    isExportControlled,
    isAssembly,
    revision,
    partNumber,
    nativeUnits,
    displayUnits,
    partFileUuid,
    partMasterVersion,
    partUuid,
    displayScale,
  }: {
    partFileName: string;
    isExportControlled: boolean;
    isAssembly: boolean;
    revision: string;
    partNumber: string;
    displayUnits: UNITS;
    nativeUnits: UNITS;
    partFileUuid: string;
    partMasterVersion: string;
    partUuid: string;
    displayScale: number;
  }) => ({
    type: types.INIT_VECTOR_EDITOR,
    payload: {
      partFileName,
      isExportControlled,
      isAssembly,
      revision,
      partNumber,
      displayUnits,
      nativeUnits,
      partFileUuid,
      partMasterVersion,
      partUuid,
      displayScale,
    },
  }),
  receiveVectorizationStatus: ({
    status,
  }: {
    status: keyof typeof PART_STATUSES | null;
  }) => ({
    type: types.RECEIVE_VECTORIZATION_STATUS,
    payload: { status },
  }),
  receiveEditorMode: ({ editorMode }: { editorMode: EDITOR_MODES }) => ({
    type: types.RECEIVE_EDITOR_MODE,
    payload: { editorMode },
  }),
  receiveForceView: ({ forceView }: { forceView: ForceView }) => ({
    type: types.RECEIVE_FORCE_VIEW,
    payload: { forceView },
  }),
  receiveMetaData: ({ metaData }: { metaData: MetaData }) => ({
    type: types.RECEIVE_META_DATA,
    payload: { metaData },
  }),
  receiveUnits: ({ displayUnits }: { displayUnits: UNITS }) => ({
    type: types.RECEIVE_UNITS,
    payload: { displayUnits },
  }),
  receiveUnitsScaleThickness: ({
    displayScale,
    displayUnits,
    thickness,
  }: {
    displayScale: number;
    displayUnits: UNITS;
    thickness: number;
  }) => ({
    type: types.RECEIVE_UNITS_SCALE_THICKNESS,
    payload: { displayScale, displayUnits, thickness },
  }),
  receiveViewerData: ({
    viewerData,
  }: {
    viewerData: ViewerDataResponseJson;
  }) => ({
    type: types.RECEIVE_VIEWER_DATA,
    payload: { viewerData },
  }),
  reset: () => ({
    type: types.RESET,
  }),
  receiveUpdatedVectors: ({
    updatedVectors,
  }: {
    updatedVectors: UpdateVectorOperation[];
  }) => ({
    type: types.RENDERER_RECEIVE_UPDATED_VECTORS,
    payload: { updatedVectors },
  }),
  initializeVectorStyleByNameMap: (obj: STEPBody) => ({
    type: types.RENDERER_INITIALIZE_VECTOR_STYLE_BY_NAME_MAP,
    payload: { obj },
  }),
  receiveSerializedDXF: ({ dxf }: { dxf: Blob }) => ({
    type: types.RENDERER_RECEIVE_SERIALIZED_DXF,
    payload: { dxf },
  }),
  receiveError: () => ({
    type: types.RENDERER_RECEIVE_ERROR,
  }),
};

// for update vector actions
export const vectorizationEditor = {
  finalizeSetScale: () => ({
    type: types.FINALIZE_SET_SCALE,
  }),
  receiveUpdatedVectors: ({
    updatedVectors,
  }: {
    updatedVectors: UpdateVectorOperation[];
  }) => ({
    type: types.EDITOR_RECEIVE_UPDATED_VECTORS,
    payload: { updatedVectors },
  }),
  updatedVectorsCounter: () => ({
    type: types.EDITOR_INCREMENT_UPDATED_VECTORS_COUNTER,
  }),
  receiveThickness: ({ thickness }: { thickness: number }) => ({
    type: types.EDITOR_RECEIVE_THICKNESS,
    payload: { thickness },
  }),
  receiveHasExportVectors: ({
    hasExportVectors,
  }: {
    hasExportVectors: boolean;
  }) => ({
    type: types.EDITOR_RECEIVE_HAS_EXPORT_VECTORS,
    payload: { hasExportVectors },
  }),
  undo: () => ({
    type: types.UNDO,
  }),
  redo: () => ({
    type: types.REDO,
  }),
};

export const saveVectorFile = {
  request: ({ saveFileName }: { saveFileName: string }) => ({
    type: types.SAVE_VECTOR_FILE_REQUEST,
    payload: { saveFileName },
  }),
  success: notify(
    () => ({
      type: types.SAVE_VECTOR_FILE_SUCCESS,
    }),
    'DXF uploaded as supporting file. Return to quote.',
  ),
  error: notify(
    (error: unknown) => ({
      type: types.SAVE_VECTOR_FILE_ERROR,
      payload: error,
    }),
    'Failed to make dxf from print',
  ),
  open: () => ({ type: types.SAVE_VECTOR_FILE_IS_MODAL_OPEN }),
  close: () => ({ type: types.SAVE_VECTOR_FILE_IS_MODAL_CLOSED }),
};

export const measurementTool = {
  enable: () => ({
    type: types.MEASUREMENT_TOOL_ENABLE,
  }),
  receiveComparison: ({ comparison }: { comparison: Comparison }) => ({
    type: types.MEASUREMENT_TOOL_RECEIVE_COMPARISON,
    payload: { comparison },
  }),
  receiveMeasurement: ({ distance }: { distance: number }) => ({
    type: types.MEASUREMENT_TOOL_RECEIVE_MEASUREMENT,
    payload: { distance },
  }),
  receiveSelections: ({ selections }: { selections: Selection[] }) => ({
    type: types.MEASUREMENT_TOOL_RECEIVE_SELECTIONS,
    payload: { selections },
  }),
  removeSelections: () => ({
    type: types.MEASUREMENT_TOOL_REMOVE_SELECTIONS,
  }),
};

export const selectorTool = {
  boxSelect: ({ selections }: { selections: Selection[] }) => ({
    type: types.SELECTOR_TOOL_BOX_SELECT,
    payload: { selections },
  }),
  clearAllSelections: () => ({
    type: types.SELECTOR_TOOL_CLEAR_ALL_SELECTIONS,
  }),
  enable: () => ({
    type: types.SELECTOR_TOOL_ENABLE,
  }),
  receiveCumulativeInfo: ({
    cumulativeInfo,
  }: {
    cumulativeInfo: Record<string, number>;
  }) => ({
    type: types.SELECTOR_TOOL_RECEIVE_CUMULATIVE_INFO,
    payload: { cumulativeInfo },
  }),
  receiveSelection: ({ selection }: { selection: Selection }) => ({
    type: types.SELECTOR_TOOL_RECEIVE_SELECTION,
    payload: { selection },
  }),
  removeSelection: ({ obj }: { obj: LiteObject['obj'] }) => ({
    type: types.SELECTOR_TOOL_REMOVE_SELECTION,
    payload: { obj },
  }),
  receiveSelections: ({ selections }: { selections: Selection[] }) => ({
    type: types.SELECTOR_TOOL_RECEIVE_SELECTIONS,
    payload: { selections },
  }),
  removeSelections: ({ objs }: { objs: LiteObject['obj'][] }) => ({
    type: types.SELECTOR_TOOL_REMOVE_SELECTIONS,
    payload: { objs },
  }),
};
